import Axios, { type AxiosResponse, isAxiosError, AxiosError, HttpStatusCode } from 'axios'

import Shared from '@/shared'
import { useLoadingStore } from '@/shared/states/loading'

const axios = Axios.create({ baseURL: process.env.NEXT_PUBLIC_MODU_API_HOST })

interface ErrorInfo {
  code: number
  message: string
}

export interface ErrorResData {
  error: ErrorInfo
  tid: string
  ts: number
}

export class ClientRequestError extends AxiosError {
  // @ts-expect-error AxiosError의 code는 string
  code: number
  withCredentials: boolean
  timeout: number
  readyState: number
  status: number
  statusText: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response: any
  responseText: string
  responseURL: string
  responseType: XMLHttpRequestResponseType
  axiosResponse: AxiosResponse | undefined

  constructor(message: string, code: number, response: AxiosResponse | undefined, request: XMLHttpRequest) {
    super(message)
    this.code = code
    this.axiosResponse = response
    this.withCredentials = request.withCredentials
    this.timeout = request.timeout
    this.readyState = request.readyState
    this.status = request.status
    this.statusText = request.statusText
    this.response = request.response
    this.responseText = request.responseText
    this.responseURL = request.responseURL
    this.responseType = request.responseType
  }
}

export class NetworkError extends AxiosError {
  axiosResponse: AxiosResponse | undefined

  constructor(message: string, response: AxiosResponse | undefined, request: XMLHttpRequest) {
    super(message)
    this.axiosResponse = response
    this.response = request.response
    this.request = request
  }
}

export const CLIENT_ERROR_MSG = '올바르지 않은 요청입니다.'

const handleNetworkError = (error: AxiosError<ErrorResData>) => {
  const errMsg = error.message
  const clientRequestNetworkError = new NetworkError(errMsg, error.response, error.request)
  window.DD_LOGS?.logger.warn(clientRequestNetworkError.message, undefined, clientRequestNetworkError)
  window.Rollbar?.warning(clientRequestNetworkError, error, clientRequestNetworkError)
  return Promise.reject(clientRequestNetworkError)
}

const handleClientRequestError = (response: AxiosResponse<ErrorResData>, error: AxiosError<ErrorResData>) => {
  const errorMsg = response.data.error.message ?? CLIENT_ERROR_MSG
  const errorCode = response.data.error.code ?? 0
  const clientRequestError = new ClientRequestError(errorMsg, errorCode, error.response, error.request)
  return Promise.reject(clientRequestError)
}

const unhandledError = (error: Error) => {
  const unhandledError = new Shared.Errors.UnknownError(error.message)
  return Promise.reject(unhandledError)
}

const { startLoading, stopLoading } = useLoadingStore.getState()
let spinnerTimeout: ReturnType<typeof setTimeout> | null = null
let spinnerActive = false
let requestCount = 0

axios.interceptors.request.use(
  (config) => {
    requestCount += 1
    if (requestCount === 1 && !spinnerTimeout && !spinnerActive) {
      spinnerTimeout = setTimeout(() => {
        spinnerActive = true
        startLoading()
        spinnerTimeout = null
      }, 200)
    }
    return config
  },
  (error) => Promise.reject(error)
)

const finishRequest = () => {
  requestCount = Math.max(0, requestCount - 1)

  if (requestCount === 0) {
    if (spinnerTimeout) {
      clearTimeout(spinnerTimeout)
      spinnerTimeout = null
    }
    if (spinnerActive) {
      spinnerActive = false
      stopLoading()
    }
  }
}

axios.interceptors.response.use(
  (res) => {
    finishRequest()
    return res
  },
  (error) => {
    finishRequest()
    if (isAxiosError<ErrorResData>(error)) {
      const hasResponse = !!error.response
      if (!hasResponse && error.message) return handleNetworkError(error)
      if (error.response && HttpStatusCode.InternalServerError > error.response.status)
        if (HttpStatusCode.BadRequest <= error.response.status) return handleClientRequestError(error.response, error)
    }
    return unhandledError(error)
  }
)
export default axios
